import React, { useState } from 'react';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Avatar,
  Button,
  TextField,
  Link,
  Grid,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper
} from '@mui/material';
import { LockReset as LockResetIcon } from '@mui/icons-material';
import { authService } from '../../services/authService';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../store/slices/uiSlice';
import { validatePassword, getPasswordPolicyDescription } from '../../utils/validationUtils';
import PasswordFieldWithSideToggle from '../../components/common/PasswordFieldWithSideToggle';

interface LocationState {
  username?: string;
}

const ResetPasswordPage: React.FC = () => {
  const location = useLocation();
  const state = location.state as LocationState;
  
  const [username, setUsername] = useState(state?.username || '');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    if (!username || !code || !newPassword || !confirmPassword) {
      setError('すべての項目を入力してください');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('新しいパスワードと確認用パスワードが一致しません');
      return;
    }
    
    // パスワードポリシーの検証
    const validationResult = validatePassword(newPassword);
    if (!validationResult.isValid) {
      setError(validationResult.errorMessage || 'パスワードが要件を満たしていません');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess(false);
    
    try {
      // パスワードリセットの確認
      const result = await authService.confirmForgotPassword(username, code, newPassword);
      
      if (result.success) {
        setSuccess(true);
        dispatch(showNotification({
          message: 'パスワードが正常にリセットされました',
          type: 'success'
        }));
        
        // 3秒後にログインページに遷移
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(result.message || 'パスワードリセットの確認中にエラーが発生しました');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'パスワードリセットの確認中にエラーが発生しました';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxWidth: 600,
        mx: 'auto',
        p: 2
      }}
    >
      <Paper sx={{ p: 4, width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
            <LockResetIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            パスワードのリセット
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, mb: 3, textAlign: 'center' }}>
            メールで受け取った確認コードと新しいパスワードを入力してください。
          </Typography>
          
          {error && <Alert severity="error" sx={{ mt: 2, width: '100%' }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mt: 2, width: '100%', mb: 2 }}>
            パスワードが正常にリセットされました。ログインページに移動します...
          </Alert>}
          
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="メールアドレス"
              name="username"
              autoComplete="email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading || success}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="code"
              label="確認コード"
              name="code"
              autoFocus={!!username}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={loading || success}
            />
            <PasswordFieldWithSideToggle
              margin="normal"
              required
              fullWidth
              name="newPassword"
              label="新しいパスワード"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={loading || success}
            />
            <PasswordFieldWithSideToggle
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="新しいパスワード（確認）"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading || success}
            />
            
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              {getPasswordPolicyDescription()}
            </Typography>
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading || success}
            >
              {loading ? <CircularProgress size={24} /> : 'パスワードをリセット'}
            </Button>
            <Grid container>
              <Grid item>
                <Link component={RouterLink} to="/login" variant="body2">
                  ログインページに戻る
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default ResetPasswordPage;
